import { useEffect, useMemo, useState } from "react";
import { Card, Stack, Typography } from "@altimateai/lego";
import { NoRulesImage } from "./assets";
import AddNewOwnership from "./AddNewOwnership";
import { Ownership, OwnershipTableRowData } from "./UserGroupDetails.types";
import OwnershipGroupedTable from "./OwnershipGroupedTable";
import OwnershipFlatTable from "./OwnershipFlatTable";
import { useQuery } from "@tanstack/react-query";
import { getTeamConflicts } from "@/helpers/apis";
import OwnershipConflictsBanner from "./OwnershipConflictsBanner";
import { useAppState } from "@/modules/app/useAppContext";

interface OwnershipSectionProps {
  ownerships?: Ownership[];
  getUserName: (userId: string) => string;
  canManageOwnership: boolean;
  isGroupDeleted: boolean;
  groupId?: string;
  refetch: () => void;
}

const OwnershipSection = ({
  ownerships,
  getUserName,
  canManageOwnership,
  isGroupDeleted,
  groupId,
  refetch,
}: OwnershipSectionProps) => {
  const { enableCostAttributionInTeams } = useAppState();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Fetch team conflicts - gracefully handle if API is not implemented
  const {
    data: teamConflicts,
    isLoading: isConflictsLoading,
    error: conflictsError,
  } = useQuery({
    queryKey: ["teamConflicts", groupId],
    queryFn: async () => {
      if (!groupId) return null;
      try {
        return await getTeamConflicts(groupId);
      } catch (err) {
        // Silently handle errors - API might not be implemented yet
        // This prevents the page from crashing
        console.warn("Failed to fetch team conflicts:", err);
        return null;
      }
    },
    enabled: !!groupId && !!ownerships?.length && enableCostAttributionInTeams,
    retry: false, // Don't retry if API is not implemented
  });

  const groupedOwnership = useMemo(() => {
    if (!ownerships?.length) return {};
    return ownerships.reduce<Record<string, Ownership[]>>((acc, ownership) => {
      const grouping = ownership.grouping || "ungrouped";
      if (!acc[grouping]) {
        acc[grouping] = [];
      }
      acc[grouping].push(ownership);
      return acc;
    }, {});
  }, [ownerships]);

  const ownershipTableData = useMemo(() => {
    if (!ownerships?.length) return [];
    const result: OwnershipTableRowData[] = [];

    const sortedOwnershipGroups = Object.entries(groupedOwnership).sort(
      ([groupingA, ownershipA], [groupingB, ownershipB]) => {
        const entityTypeA = (ownershipA as Ownership[])[0]?.entity_type || "";
        const entityTypeB = (ownershipB as Ownership[])[0]?.entity_type || "";

        if (entityTypeA !== entityTypeB) {
          return entityTypeA.localeCompare(entityTypeB);
        }
        return groupingA.localeCompare(groupingB);
      }
    );

    sortedOwnershipGroups.forEach(([grouping, ownershipGroup], groupIndex) => {
      const ownershipArray = ownershipGroup as Ownership[];
      if (!ownershipArray.length) return;

      const sortedOwnerships = [...ownershipArray].sort((a, b) => {
        if (a.entity_type !== b.entity_type) {
          return a.entity_type.localeCompare(b.entity_type);
        }
        return a.ownership_rule.condition_key.localeCompare(b.ownership_rule.condition_key);
      });

      const headerItem = {
        ...sortedOwnerships[0],
        count: sortedOwnerships.length,
        isGroupHeader: true,
        ruleOperator: (groupIndex === 0 ? "WHERE" : "OR") as "WHERE" | "AND" | "OR",
      };
      result.push(headerItem);

      if (expandedGroups[grouping]) {
        sortedOwnerships.slice(1).forEach(ownership => {
          result.push({
            ...ownership,
            ruleOperator: "AND",
          });
        });
      }
    });

    return result;
  }, [ownerships, groupedOwnership, expandedGroups]);

  useEffect(() => {
    const groupKeys = Object.keys(groupedOwnership);
    if (!groupKeys.length) return;
    setExpandedGroups(prev => {
      let changed = false;
      const next = { ...prev };
      groupKeys.forEach(grouping => {
        const key = grouping || "ungrouped";
        if (typeof next[key] === "undefined") {
          next[key] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [groupedOwnership]);

  const nonGroupedOwnershipData = useMemo(() => {
    if (!ownerships?.length) return [];
    return ownerships.map((ownership, index) => ({
      ...ownership,
      ruleOperator: (index === 0 ? "WHERE" : "OR") as "WHERE" | "AND" | "OR",
    }));
  }, [ownerships]);

  const hasGroupedOwnership =
    Boolean(ownerships?.length) &&
    Object.keys(groupedOwnership).length > 0 &&
    ownershipTableData.length > 0;

  const toggleGroupExpansion = (groupName: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  if (!ownerships?.length) {
    return (
      <section aria-label="Ownership" className="al-bg-light al-p-3 al-rounded al-mt-4">
        <Stack className="al-justify-between al-mb-3">
          <Typography variant="section-heading">Ownership</Typography>
          {!isGroupDeleted && canManageOwnership && groupId && (
            <AddNewOwnership groupId={groupId} />
          )}
        </Stack>
        <Card>
          <Stack direction="column" justify="center" align="center" className="al-p-4 al-rounded">
            <NoRulesImage />
            No ownership rules found!
          </Stack>
        </Card>
      </section>
    );
  }

  return (
    <section aria-label="Ownership" className="al-bg-light al-p-3 al-rounded al-mt-4">
      <Stack className="al-justify-between al-mb-3">
        <Typography variant="section-heading">Ownership</Typography>
        {!isGroupDeleted && canManageOwnership && groupId && <AddNewOwnership groupId={groupId} />}
      </Stack>
      <div className="al-bg-white">
        <OwnershipConflictsBanner
          conflicts={teamConflicts || null}
          isLoading={isConflictsLoading}
          error={conflictsError ? "Failed to load conflicts" : null}
        />
        {hasGroupedOwnership ? (
          <OwnershipGroupedTable
            items={ownershipTableData}
            expandedGroups={expandedGroups}
            toggleGroup={toggleGroupExpansion}
            getUserName={getUserName}
            canManageOwnership={canManageOwnership}
            groupId={groupId}
            refetch={refetch}
          />
        ) : (
          <OwnershipFlatTable
            items={nonGroupedOwnershipData}
            getUserName={getUserName}
            canManageOwnership={canManageOwnership}
            groupId={groupId}
            refetch={refetch}
          />
        )}
      </div>
    </section>
  );
};

export default OwnershipSection;
